import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Wallet, Users, BarChart3, RefreshCw, Settings, Target, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

function NavItem({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button 
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors hover:bg-muted flex-shrink-0 min-w-[60px] ${active ? 'text-primary bg-primary/10' : 'text-muted-foreground'}`}
      data-testid={testId}
    >
      <Icon className="w-5 h-5" />
      <span className="text-xs font-medium whitespace-nowrap">{label}</span>
    </button>
  );
}

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const scrollRef = useRef(null);
  
  const currentPath = location.pathname;
  const isChild = user?.family_role === 'child';
  const isSingle = user?.situation === 'single';
  const shouldHideFamily = isChild || isSingle;

  // Get translated label for subscriptions - use full word in each language
  const getSubscriptionsLabel = () => {
    if (language === 'fr') return 'Abonnements';
    if (language === 'pt') return 'Assinaturas';
    return 'Subscriptions';
  };

  // Define nav items for scroll position calculation
  const navItems = [
    { path: '/dashboard', testId: 'nav-dashboard' },
    { path: '/transactions', testId: 'nav-transactions' },
    { path: '/objectives', testId: 'nav-objectives' },
    { path: '/budgets', testId: 'nav-budgets' },
    ...(!shouldHideFamily ? [{ path: '/family', testId: 'nav-family' }] : []),
    ...(!isChild ? [{ path: '/subscriptions', testId: 'nav-subscriptions' }] : []),
    { path: '/settings', testId: 'nav-settings' },
  ];

  // Restore scroll position when component mounts
  useEffect(() => {
    if (scrollRef.current) {
      const savedScroll = sessionStorage.getItem('bottomNavScroll');
      if (savedScroll) {
        scrollRef.current.scrollLeft = parseInt(savedScroll, 10);
      }
    }
  }, []);

  // Scroll to active item when path changes
  useEffect(() => {
    if (scrollRef.current) {
      const activeIndex = navItems.findIndex(item => item.path === currentPath);
      if (activeIndex >= 0) {
        const buttons = scrollRef.current.querySelectorAll('button');
        if (buttons[activeIndex]) {
          const button = buttons[activeIndex];
          const container = scrollRef.current;
          const buttonLeft = button.offsetLeft;
          const buttonWidth = button.offsetWidth;
          const containerWidth = container.offsetWidth;
          
          // Center the button in the container
          const targetScroll = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
          container.scrollTo({ left: targetScroll, behavior: 'smooth' });
          
          // Save scroll position
          setTimeout(() => {
            sessionStorage.setItem('bottomNavScroll', container.scrollLeft.toString());
          }, 300);
        }
      }
    }
  }, [currentPath, navItems]);

  // Save scroll position on scroll
  const handleScroll = () => {
    if (scrollRef.current) {
      sessionStorage.setItem('bottomNavScroll', scrollRef.current.scrollLeft.toString());
    }
  };

  return (
    <nav className="mobile-nav" data-testid="bottom-nav">
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex items-center gap-1 max-w-full mx-auto overflow-x-auto scrollbar-hide px-2"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <NavItem 
          icon={Home} 
          label={t('dashboard')} 
          active={currentPath === '/dashboard'} 
          onClick={() => navigate('/dashboard')} 
          testId="nav-dashboard" 
        />
        <NavItem 
          icon={Wallet} 
          label={t('transactions')} 
          active={currentPath === '/transactions'} 
          onClick={() => navigate('/transactions')} 
          testId="nav-transactions" 
        />
        <NavItem 
          icon={Target} 
          label={t('objectives')} 
          active={currentPath === '/objectives'} 
          onClick={() => navigate('/objectives')} 
          testId="nav-objectives" 
        />
        <NavItem 
          icon={BarChart3} 
          label={t('budgets')} 
          active={currentPath === '/budgets'} 
          onClick={() => navigate('/budgets')} 
          testId="nav-budgets" 
        />
        {/* Hide family page for children and single users */}
        {!shouldHideFamily && (
          <NavItem 
            icon={Users} 
            label={t('family')} 
            active={currentPath === '/family'} 
            onClick={() => navigate('/family')} 
            testId="nav-family" 
          />
        )}
        {/* Hide subscriptions for children */}
        {!isChild && (
          <NavItem 
            icon={RefreshCw} 
            label={getSubscriptionsLabel()} 
            active={currentPath === '/subscriptions'} 
            onClick={() => navigate('/subscriptions')} 
            testId="nav-subscriptions" 
          />
        )}
        {/* Invoices/Receipts page - hide for children */}
        {!isChild && (
          <NavItem 
            icon={FileText} 
            label={t('invoices')} 
            active={currentPath === '/invoices'} 
            onClick={() => navigate('/invoices')} 
            testId="nav-invoices" 
          />
        )}
        <NavItem 
          icon={Settings} 
          label={t('settings')} 
          active={currentPath === '/settings'} 
          onClick={() => navigate('/settings')} 
          testId="nav-settings" 
        />
      </div>
    </nav>
  );
}
